import { useCallback, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {
  IconCopy,
  IconDotsVertical,
  IconGitBranch,
  IconPencil,
  IconPlus,
  IconTemplate,
  IconTrash,
} from '@tabler/icons-react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { defaultFlowVoiceConfig } from '@legacy-shared/widgets/core/voicePresets';
import { ErrorAlert } from '@/components/error-alert';
import { TemplateBrowser } from '@/components/template-browser';
import { TemplateVariableWizard } from '@/components/template-variable-wizard';
import { useOrg } from '@/hooks/use-org';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { isTemplatesEnabled } from '@/lib/features';
import { readStoredNodes } from './-components/flow-data';
import { CreateFlowMutation, FlowsListSubscription, RenameFlowMutation } from './-components/graphql';

export const Route = createFileRoute('/_authed/flows/')({
  component: FlowsPage,
});

type FlowSummary = {
  id: string;
  name: string;
  description: string | null;
  isPublished: boolean;
  nodeCount: number;
};

function newStartNode() {
  const id = `start-${Date.now()}`;

  return { id, type: 'start', position: { x: 250, y: 50 }, data: { config: {} } };
}

function FlowCard({
  flow,
  onRename,
  onDuplicate,
  onDelete,
}: {
  flow: FlowSummary;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return (
    <Paper
      p="lg"
      radius="lg"
      withBorder
      style={{ textDecoration: 'none' }}
      renderRoot={(props) => <Link to="/flows/$flowId" params={{ flowId: flow.id }} {...props} />}
    >
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
            <IconGitBranch
              size={20}
              stroke={1.5}
              style={{ color: 'var(--mantine-primary-color-filled)', flexShrink: 0 }}
            />
            <Text fw={600} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {flow.name}
            </Text>
          </Group>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" onClick={(e) => e.preventDefault()}>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={(e) => {
                  e.preventDefault();
                  onRename();
                }}
              >
                Rename
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCopy size={14} />}
                onClick={(e) => {
                  e.preventDefault();
                  onDuplicate();
                }}
              >
                Duplicate
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete();
                }}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {flow.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {flow.description}
          </Text>
        )}

        <Group gap="xs">
          <Badge size="sm" variant="light" color={flow.isPublished ? 'green' : 'yellow'}>
            {flow.isPublished ? 'Published' : 'Draft'}
          </Badge>
          <Text size="xs" c="dimmed">
            {flow.nodeCount} nodes
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}

function NewFlowCard({ onClick }: { onClick: () => void }) {
  return (
    <Paper
      p="lg"
      radius="lg"
      withBorder
      onClick={onClick}
      style={{
        borderStyle: 'dashed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 140,
        cursor: 'pointer',
      }}
    >
      <Stack align="center" gap="xs">
        <IconPlus size={24} style={{ color: 'var(--mantine-color-dimmed)' }} />
        <Text size="sm" c="dimmed">
          Create new flow
        </Text>
      </Stack>
    </Paper>
  );
}

function FlowsPage() {
  const navigate = useNavigate();
  const { organizationId, isLoading: isOrgLoading } = useOrg();

  const [{ data, error: listError }] = useSubscription({
    query: FlowsListSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const [, runCreateFlow] = useMutation(CreateFlowMutation);
  const [, runRenameFlow] = useMutation(RenameFlowMutation);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [renameFlowId, setRenameFlowId] = useState<string | null>(null);
  const [deleteFlowId, setDeleteFlowId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const flows: FlowSummary[] = (data?.flows ?? []).map((f) => ({
    id: f.id,
    name: f.name,
    description: f.description,
    isPublished: f.currentPublicationId !== null,
    nodeCount: readStoredNodes(f.nodes).length,
  }));

  const isNameTaken = (candidate: string, excludedFlowId?: string) =>
    flows.some((f) => f.id !== excludedFlowId && f.name.trim().toLowerCase() === candidate.trim().toLowerCase());

  const closeCreateModal = () => {
    setModalError(null);
    setCreateModalOpen(false);
  };

  const create = async () => {
    if (!organizationId || !name.trim()) return;

    setModalError(null);
    setPageError(null);

    if (isNameTaken(name)) {
      setModalError('A flow with this name already exists');

      return;
    }

    setIsSubmitting(true);

    const startNode = newStartNode();
    const result = await runCreateFlow({
      organizationId,
      name: name.trim(),
      description: description.trim() || null,
      nodes: [startNode],
      edges: [],
      entryNodeId: startNode.id,
      voiceConfig: { ...defaultFlowVoiceConfig },
    });

    setIsSubmitting(false);

    const createdId = result.data?.insertFlowsOne?.id;

    if (!createdId) {
      setModalError(getErrorMessage(result.error, 'Failed to create flow'));

      return;
    }

    closeCreateModal();
    setName('');
    setDescription('');
    void navigate({ to: '/flows/$flowId', params: { flowId: createdId } });
  };

  const rename = async () => {
    if (!renameFlowId || !name.trim()) return;

    setModalError(null);
    setPageError(null);

    if (isNameTaken(name, renameFlowId)) {
      setModalError('A flow with this name already exists');

      return;
    }

    setIsSubmitting(true);

    const result = await runRenameFlow({ flowId: renameFlowId, name: name.trim() });

    setIsSubmitting(false);

    if (result.error) {
      setModalError(getErrorMessage(result.error, 'Failed to rename flow'));

      return;
    }

    setRenameFlowId(null);
    setName('');
  };

  const duplicate = useCallback(
    async (flowId: string) => {
      if (isSubmitting) return;

      setModalError(null);
      setPageError(null);
      setIsSubmitting(true);

      const result = await api.flows({ flowId }).duplicate.post();

      setIsSubmitting(false);

      if (result.error) {
        setPageError(getErrorMessage(result.error, 'Failed to duplicate flow'));

        return;
      }

      void navigate({ to: '/flows/$flowId', params: { flowId: result.data.id } });
    },
    [isSubmitting, navigate],
  );

  const remove = async () => {
    if (!deleteFlowId) return;

    setModalError(null);
    setPageError(null);
    setIsSubmitting(true);

    const result = await api.flows({ flowId: deleteFlowId }).delete();

    setIsSubmitting(false);

    if (result.error) {
      setModalError(getErrorMessage(result.error, 'Failed to delete flow'));

      return;
    }

    setDeleteFlowId(null);
  };

  if (isOrgLoading || (organizationId && data === undefined && !listError)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>Call Flows</Title>
              <Text c="dimmed" mt="xs">
                Build and manage your IVR call flows
              </Text>
            </div>
            <Group gap="xs">
              {isTemplatesEnabled() && (
                <Button
                  variant="light"
                  leftSection={<IconTemplate size={16} />}
                  onClick={() => setTemplateBrowserOpen(true)}
                >
                  From Template
                </Button>
              )}
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setModalError(null);
                  setCreateModalOpen(true);
                }}
              >
                New Flow
              </Button>
            </Group>
          </Group>

          <ErrorAlert message={pageError ?? (listError ? getErrorMessage(listError, 'Failed to load flows') : null)} />

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {flows.map((flow) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                onRename={() => {
                  setModalError(null);
                  setName(flow.name);
                  setRenameFlowId(flow.id);
                }}
                onDuplicate={() => void duplicate(flow.id)}
                onDelete={() => {
                  setModalError(null);
                  setDeleteFlowId(flow.id);
                }}
              />
            ))}
            <NewFlowCard
              onClick={() => {
                setModalError(null);
                setCreateModalOpen(true);
              }}
            />
          </SimpleGrid>
        </Stack>
      </Container>

      <Modal opened={createModalOpen} onClose={closeCreateModal} title="Create New Flow">
        <Stack>
          <ErrorAlert message={modalError} />
          <TextInput
            label="Name"
            placeholder="e.g. Main Menu"
            value={name}
            onChange={(e) => {
              setModalError(null);
              setName(e.target.value);
            }}
            required
          />
          <Textarea
            label="Description"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button onClick={() => void create()} loading={isSubmitting} disabled={!name.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={renameFlowId !== null}
        onClose={() => {
          setModalError(null);
          setRenameFlowId(null);
        }}
        title="Rename Flow"
      >
        <Stack>
          <ErrorAlert message={modalError} />
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => {
              setModalError(null);
              setName(e.target.value);
            }}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRenameFlowId(null)}>
              Cancel
            </Button>
            <Button onClick={() => void rename()} loading={isSubmitting} disabled={!name.trim()}>
              Rename
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteFlowId !== null}
        onClose={() => {
          setModalError(null);
          setDeleteFlowId(null);
        }}
        title="Delete Flow"
      >
        <Stack>
          <Text>Are you sure you want to delete this flow? This action cannot be undone.</Text>
          <ErrorAlert message={modalError} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteFlowId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void remove()} loading={isSubmitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={templateBrowserOpen && !selectedTemplateId}
        onClose={() => setTemplateBrowserOpen(false)}
        title="Choose a Flow Template"
        size="lg"
      >
        <TemplateBrowser
          type="flow"
          onSelect={(id) => setSelectedTemplateId(id)}
          onCancel={() => setTemplateBrowserOpen(false)}
        />
      </Modal>

      <Modal
        opened={selectedTemplateId !== null}
        onClose={() => {
          setSelectedTemplateId(null);
          setTemplateBrowserOpen(false);
        }}
        title="Configure Template"
        size="lg"
      >
        {selectedTemplateId && (
          <TemplateVariableWizard
            templateId={selectedTemplateId}
            type="flow"
            organizationId={organizationId}
            onComplete={(newId) => {
              setSelectedTemplateId(null);
              setTemplateBrowserOpen(false);
              void navigate({ to: '/flows/$flowId', params: { flowId: newId } });
            }}
            onBack={() => setSelectedTemplateId(null)}
          />
        )}
      </Modal>
    </>
  );
}
